import { useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useApi, useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  LoadingButton,
  SearchSelect,
} from '@fortem-monorepo/react-ui'
import { MarketSummary } from '@fortem-monorepo/web-sdk'

import { useMarketSummaryContext } from '@/pages/instruments/market-summary/market-summary-context'
import { useUpdateInstrumentMutation } from '@/pages/instruments/market-summary/market-summary.mutations'

import { instrumentUnitFields, updateInstrumentSchema } from './instrument.schema'

export interface UpdateInstrumentDialogProps extends DialogProps {
  mutationParams: { instrument_id: number }
  onClose: () => void
}

export const UpdateInstrumentDialog: React.FC<UpdateInstrumentDialogProps> = ({
  mutationParams,
  onClose,
  ...props
}) => {
  const api = useApi()
  const { instrumentsQuery, hasWriteAccess } = useMarketSummaryContext()
  const instrument = instrumentsQuery.data?.find((row) => row.instrument_id === mutationParams.instrument_id)
  const form = useFormWithSchema({ mode: 'all' }, updateInstrumentSchema)
  const { reset, trigger } = form
  const updateMutation = useUpdateInstrumentMutation()
  const pending = form.formState.isSubmitting || updateMutation.isPending
  const dirty = form.formState.isDirty

  const bucketsQuery = useQuery({
    queryKey: ['market-summary', 'buckets'],
    queryFn: () => api.marketSummary.getBuckets(),
    enabled: Boolean(props.open),
  })
  const subBucketsQuery = useQuery({
    queryKey: ['market-summary', 'sub-buckets'],
    queryFn: () => api.marketSummary.getSubBuckets(),
    enabled: Boolean(props.open),
  })
  const valueTypesQuery = useQuery({
    queryKey: ['market-summary', 'value-types'],
    queryFn: () => api.marketSummary.getValueTypes(),
    enabled: Boolean(props.open),
  })

  useEffect(() => {
    if (props.open && instrument && !dirty) {
      reset({
        section: instrument.section as MarketSummary.Section,
        bucket: instrument.bucket ?? null,
        sub_bucket: instrument.sub_bucket ?? null,
        value_type: instrument.value_type,
        source_unit: instrument.source_unit ?? null,
        display_metric_unit: instrument.display_metric_unit ?? null,
      })
      void trigger()
    }
  }, [props.open, instrument, dirty, reset, trigger])

  const onSubmit = form.handleSubmit(async (data) => {
    if (!instrument || !hasWriteAccess || pending) return
    try {
      await updateMutation.mutateAsync({ ...data, instrument_id: mutationParams.instrument_id })
      onClose()
    } catch {
      // Keep the edits available for retry; the mutation displays the error.
      return
    }
  })

  const fields = [
    {
      name: 'section',
      label: 'Section',
      values: MarketSummary.SECTIONS,
      optional: false,
      loading: false,
      error: false,
    },
    {
      name: 'bucket',
      label: 'Bucket',
      values: bucketsQuery.data ?? [],
      optional: true,
      loading: bucketsQuery.isLoading,
      error: bucketsQuery.isError,
    },
    {
      name: 'sub_bucket',
      label: 'Sub Bucket',
      values: subBucketsQuery.data ?? [],
      optional: true,
      loading: subBucketsQuery.isLoading,
      error: subBucketsQuery.isError,
    },
    {
      name: 'value_type',
      label: 'Value Type',
      values: valueTypesQuery.data ?? [],
      optional: false,
      loading: valueTypesQuery.isLoading,
      error: valueTypesQuery.isError,
    },
    ...instrumentUnitFields.map((field) => ({
      ...field,
      values: MarketSummary.INSTRUMENT_UNITS,
      optional: false,
      loading: false,
      error: false,
    })),
  ] as const

  return (
    <Dialog
      {...props}
      onOpenChange={(open) => {
        if (!open && !pending) onClose()
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update Instrument</DialogTitle>
          <DialogDescription>Changes apply to this instrument across all Market Summary displays.</DialogDescription>
        </DialogHeader>
        <p className="text-sm font-medium">Instrument {mutationParams.instrument_id}</p>
        {!instrument && (
          <p className="text-sm text-destructive" role="alert">
            {instrumentsQuery.isLoading ? 'Loading instrument...' : 'Instrument metadata is unavailable.'}
          </p>
        )}
        <Form {...form}>
          <div className="space-y-4">
            {fields.map((config) => (
              <FormField
                key={config.name}
                control={form.control}
                name={config.name}
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>{config.label}</FormLabel>
                      <FormError />
                    </div>
                    <SearchSelect
                      value={field.value ?? undefined}
                      setValue={(value) => field.onChange(value ?? null)}
                      showNone={config.optional ? 'None' : undefined}
                      options={config.values.map((value) => ({ label: value, value }))}
                      placeholder={`Select ${config.label.toLowerCase()}...`}
                      loading={config.loading}
                      disabled={!instrument || !hasWriteAccess || pending || config.loading || config.error}
                    />
                    {config.error && (
                      <p className="text-sm text-destructive" role="alert">
                        Unable to load {config.label.toLowerCase()} options.
                      </p>
                    )}
                  </FormItem>
                )}
              />
            ))}
          </div>
        </Form>
        <DialogFooter>
          <div className="flex items-center gap-2">
            <Button size="sm" type="button" variant="destructive" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <LoadingButton
              size="sm"
              onClick={onSubmit}
              disabled={!instrument || !hasWriteAccess || !form.formState.isValid || !form.formState.isDirty || pending}
            >
              Save Changes
            </LoadingButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
